import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Truck, RefreshCcw, AlertTriangle, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import driverService from '../services/driverService';
import VehicleProfilePage from './VehicleProfilePage';

const DRIVER_ALLOWED_TABS = ['details', 'inspections', 'maintenance'];

const resolveAssignedVehicleId = (driverRecord) => {
  if (!driverRecord) {
    return null;
  }

  return driverRecord.currentVehicleId
    || driverRecord.assignedVehicleId
    || (Array.isArray(driverRecord.assignedVehicles) ? driverRecord.assignedVehicles[0] : null)
    || driverRecord.vehicleId
    || null;
};

const DriverVehiclePage = () => {
  const { userData, company, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [driverRecord, setDriverRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  const companyId = useMemo(() => {
    if (company?.id) return company.id;
    if (company?.companyId) return company.companyId;
    if (userData?.companyId) return userData.companyId;
    if (userData?.company?.id) return userData.company.id;
    return null;
  }, [company, userData]);

  const normalizedEmail = userData?.email?.toLowerCase() || '';
  // Primitive deps: the auth profile object is replaced on every refresh.
  const driverLookup = useMemo(
    () => ({ uid: userData?.uid, email: userData?.email, driverId: userData?.driverId }),
    [userData?.uid, userData?.email, userData?.driverId]
  );
  const isDriver = userData?.role === 'driver';
  const fromPortal = location.state?.fromPortal === true;
  const BRAND_NAME = 'Himil Fleet';
  const BRAND_TAGLINE = 'Driver workspace powered by Himil Fleet';
  const BRAND_LOGO_SRC = '/Himil-logo.png';

  const fetchDriverRecord = useCallback(async () => {
    if (!companyId || !normalizedEmail) {
      setDriverRecord(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setPageError('');

    try {
      const matchedDriver = await driverService.findDriverForUser(driverLookup, { companyId });

      setDriverRecord(matchedDriver || null);
    } catch (error) {
      console.error('Failed to load driver details:', error);
      setPageError(error.getUserFriendlyMessage?.() || 'Unable to load driver information.');
      setDriverRecord(null);
    } finally {
      setLoading(false);
    }
  }, [companyId, normalizedEmail, driverLookup]);

  useEffect(() => {
    fetchDriverRecord();
  }, [fetchDriverRecord]);

  const handleLogout = useCallback(async () => {
    setLogoutError('');
    setIsLoggingOut(true);

    try {
      const result = await logout();

      if (!result?.success) {
        throw new Error(result?.error || 'Logout failed. Please try again.');
      }

      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Driver vehicle logout failed:', error);
      setLogoutError(error?.message || 'Unable to log out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  }, [logout, navigate]);

  if (!isDriver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">This page is only available to driver accounts.</p>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-gray-800">Company context missing</p>
          <p className="text-sm text-gray-600">We could not determine which company you belong to. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your assigned vehicle...</p>
        </div>
      </div>
    );
  }

  const assignedVehicleId = resolveAssignedVehicleId(driverRecord);

  if (!driverRecord || !assignedVehicleId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white shadow rounded-lg p-6 space-y-3 text-center">
          <AlertTriangle className="h-6 w-6 text-orange-500 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-900">No assigned vehicle</h2>
          <p className="text-sm text-gray-600">
            We could not find a vehicle assigned to your driver profile. Please contact your dispatcher or fleet manager.
          </p>
          {!fromPortal && (
            <Link
              to="/driver-portal"
              state={{ fromVehicle: true }}
              className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
            >
              View Your Info
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <img src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} className="h-10 w-auto mx-auto sm:mx-0" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-semibold uppercase text-indigo-600 tracking-wide">{BRAND_NAME}</p>
              <p className="text-sm text-gray-600">{BRAND_TAGLINE}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
            <Link
              to="/driver-portal"
              state={{ fromVehicle: true }}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 w-full sm:w-auto"
            >
              <User className="h-4 w-4 mr-2" />
              View Your Info
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-70 w-full sm:w-auto"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {isLoggingOut ? 'Logging out...' : 'Logout'}
            </button>
          </div>
        </div>
        {logoutError && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {logoutError}
          </div>
        )}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-center sm:text-left">
              <div className="p-2 bg-blue-100 rounded-lg mx-auto sm:mx-0">
                <Truck className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">Assigned Vehicle</p>
                <h1 className="text-3xl font-bold text-gray-900 mt-1">{driverRecord?.assignedVehicleLabel || 'Your Vehicle'}</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Viewing vehicle {assignedVehicleId}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
              <button
                type="button"
                onClick={fetchDriverRecord}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>

          {pageError && (
            <div className="mt-4 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {pageError}
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className='m-5'>
            <VehicleProfilePage
              vehicleId={assignedVehicleId}
              allowedTabs={DRIVER_ALLOWED_TABS}
              readOnly
              allowInspectionActions
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverVehiclePage;
